import { Component, inject, signal, computed, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { RouterLink, ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ApiService, showToast } from './api.service';
import { TranslatePipe } from './i18n.service';

interface LabourLine {
  workerId: number;
  name: string;
  rate: number;
  hours: number;
  workDetails: string;
}

@Component({
  selector: 'app-field-quoting',
  standalone: true,
  imports: [RouterLink, CommonModule, CurrencyPipe, FormsModule, TranslatePipe],
  template: `
    <div class="min-h-screen flex flex-col pb-24" style="background:#F7FBFD;">
      <!-- Header -->
      <header class="px-5 pt-4 pb-4 sticky top-0 z-40 text-white shadow-sm flex items-center gap-3"
              style="background:linear-gradient(135deg,#0F334A 0%,#145B7A 100%);">
        <a [routerLink]="backRoute()" class="w-10 h-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-white/90 active:bg-white/15 shrink-0">
          <i class="ti ti-arrow-left text-[20px]"></i>
        </a>
        <div class="flex-1 min-w-0">
          <p class="text-[10px] font-medium text-white/55 uppercase tracking-widest">{{ 'inspection' | t }}</p>
          <h1 class="text-lg font-semibold text-white/95 truncate">{{ 'buildQuote' | t }}</h1>
        </div>
      </header>

      @if (isLoading()) {
        <div class="flex-1 flex flex-col items-center justify-center p-8">
          <i class="ti ti-loader animate-spin text-4xl text-[#2B9AD2] mb-4"></i>
          <p class="font-bold text-slate-500">Preparing quote context...</p>
        </div>
      } @else if (!canBuildQuote()) {
        <div class="flex-1 flex flex-col items-center justify-center p-10 text-center">
          <div class="w-20 h-20 bg-amber-50 text-amber-500 rounded-full flex items-center justify-center mb-6 border border-amber-100 shadow-sm">
            <i class="ti ti-alert-circle text-5xl"></i>
          </div>
          <h2 class="text-2xl font-semibold text-slate-900 mb-2">Job Not Ready</h2>
          <p class="text-slate-500 leading-relaxed max-w-[280px]">
            Start the job or complete the field work before generating this quote.
          </p>
          <a [routerLink]="backRoute()" class="mt-8 px-8 h-12 bg-[#102C41] text-white rounded-xl font-bold flex items-center justify-center shadow-lg">
            GO BACK TO JOB
          </a>
        </div>
      } @else {
        <main class="px-4 pt-5 space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500">
          
          <!-- Section: Labour -->
          <div class="space-y-4">
            <div class="flex items-center justify-between px-1">
               <h2 class="text-xs font-semibold text-slate-400 uppercase tracking-widest">Labour Summary</h2>
               <span class="text-[10px] font-bold text-[#2B9AD2] bg-[#F1F9FE] px-2 py-0.5 rounded border border-[#E4F1FA]">{{ durationHours() }}h Working Time</span>
            </div>
            
            <div class="space-y-3">
              @for (line of labourLines(); track $index) {
                <div class="bg-white rounded-3xl p-5 shadow-sm border border-[#EEF2F7]">
                  <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-3">
                      <div class="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                        <i class="ti ti-user text-xl"></i>
                      </div>
                      <div>
                        <h4 class="font-bold text-slate-900">{{ line.name }}</h4>
                        <p class="text-[10px] font-bold text-slate-400 uppercase">{{ line.rate | currency:'NZD' }}{{ isFixedRateJob() ? ' fixed charge' : '/hr' }}</p>
                      </div>
                    </div>
                    <div class="text-right">
                      <div class="font-semibold text-slate-900 text-lg">{{ line.rate * line.hours | currency:'NZD' }}</div>
                    </div>
                  </div>
                  <input type="text" [(ngModel)]="line.workDetails" placeholder="Describe work performed..." 
                         class="w-full h-12 px-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-medium focus:bg-white focus:border-[#2B9AD2] outline-none transition-all">
                </div>
              }
            </div>
          </div>

          <!-- Section: Materials -->
          <div class="space-y-4">
            <div class="flex items-center justify-between px-1">
               <h2 class="text-xs font-semibold text-slate-400 uppercase tracking-widest">Materials & Parts</h2>
               <button *ngIf="canEditQuote()" (click)="openPicker()" class="text-[10px] font-semibold text-[#2B9AD2] uppercase tracking-wider flex items-center gap-1">
                 <i class="ti ti-plus"></i> Add Extra Part
               </button>
            </div>

            <div class="bg-white rounded-2xl shadow-sm border border-[#EEF2F7] overflow-hidden">
              <div class="divide-y divide-slate-50">
                @for (item of cartItems(); track $index) {
                  <div class="p-4 flex gap-4 items-center animate-in slide-in-from-right-4 duration-300">
                    <div class="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 overflow-hidden flex items-center justify-center shrink-0">
                      <img [src]="item.photo_url || defaultPhoto" class="w-full h-full object-cover">
                    </div>
                    <div class="flex-1 min-w-0">
                      <h4 class="font-bold text-slate-900 truncate leading-tight">{{ item.name }}</h4>
                      <div class="flex items-center justify-between mt-1">
                        <div class="text-xs font-bold text-slate-400">{{ item.quantity }} x {{ item.sell_price | currency:'NZD' }}</div>
                        <div class="font-semibold text-slate-900">{{ item.quantity * item.sell_price | currency:'NZD' }}</div>
                      </div>
                    </div>
                    <button (click)="removeMaterial($index)" class="w-8 h-8 rounded-full text-slate-300 active:text-red-500">
                      <i class="ti ti-trash"></i>
                    </button>
                  </div>
                }
                @if (cartItems().length === 0) {
                  <div class="p-10 text-center">
                    <i class="ti ti-box text-4xl text-slate-100 mb-2 block"></i>
                    <p class="text-xs font-bold text-slate-300">No materials imported</p>
                  </div>
                }
              </div>
              
              <!-- Sub-Totals -->
              <div class="bg-[#F8FAFC] p-6 border-t border-slate-100 space-y-3">
                <div class="flex justify-between text-slate-500 font-bold text-sm">
                  <span>Sub-Total</span>
                  <span>{{ subTotal() | currency:'NZD' }}</span>
                </div>
                <div class="flex justify-between text-slate-500 font-bold text-sm">
                  <span>GST (15%)</span>
                  <span>{{ subTotal() * 0.15 | currency:'NZD' }}</span>
                </div>
                <div class="flex justify-between text-2xl font-semibold text-slate-900 pt-4 border-t border-slate-200 mt-2">
                  <span class="tracking-tight">Grand Total</span>
                  <span class="text-[#2B9AD2]">{{ subTotal() * 1.15 | currency:'NZD' }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Section: Signature -->
          <div *ngIf="canEditQuote()" class="space-y-4">
            <h2 class="text-xs font-semibold text-slate-400 uppercase tracking-widest px-1">Plumber Verification</h2>
            <div class="bg-white rounded-2xl p-6 shadow-sm border border-[#EEF2F7] space-y-4">
              <p class="text-xs text-slate-500 leading-relaxed font-medium">
                I hereby verify that the labour hours and materials logged above are a true reflection of the work performed on behalf of <strong>NZ Plumbing Limited</strong>.
              </p>
              
              <div class="border-2 border-dashed border-slate-200 rounded-3xl bg-slate-50/50 relative overflow-hidden h-40">
                <canvas #signatureCanvas class="w-full h-full touch-none"></canvas>
                @if (!hasSignature()) {
                  <div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none opacity-20">
                    <i class="ti ti-pencil text-3xl mb-1"></i>
                    <span class="font-semibold text-xs uppercase tracking-tighter">Sign Here</span>
                  </div>
                }
              </div>
              
              <div class="flex justify-between items-center">
                <button (click)="clearSignature()" class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                  Clear Signature
                </button>
                <div class="flex items-center gap-2">
                   <div class="w-2 h-2 rounded-full" [class.bg-emerald-500]="hasSignature()" [class.bg-slate-200]="!hasSignature()"></div>
                   <span class="text-[10px] font-bold text-slate-400 uppercase">{{ hasSignature() ? 'Verified' : 'Required' }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Signed Indicator -->
          <div *ngIf="isSigned()" class="bg-emerald-50 border border-emerald-100 rounded-3xl p-6 flex items-center gap-4 animate-in fade-in duration-500">
             <div class="w-12 h-12 rounded-2xl bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-lg shadow-emerald-200">
                <i class="ti ti-circle-check text-2xl"></i>
             </div>
             <div>
                <h4 class="font-semibold text-slate-900 leading-tight">Quote Signed & Locked</h4>
                <p class="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mt-1">Legally verified by plumber</p>
             </div>
          </div>

          <!-- Bottom Action -->
          <button *ngIf="canEditQuote()" (click)="submitQuote()" 
                  [disabled]="!hasSignature() || isSaving()"
                  class="w-full h-16 rounded-2xl font-semibold text-lg flex items-center justify-center gap-3 transition-all transform active:scale-[0.98] shadow-lg disabled:opacity-50 text-white"
                  style="background: linear-gradient(135deg, #0b3146, #0b6c99);">
            @if (isSaving()) {
              <i class="ti ti-loader animate-spin text-2xl"></i> GENERATING PDF...
            } @else {
              <i class="ti ti-file-invoice text-2xl"></i>
              APPROVE & GENERATE PDF
            }
          </button>

          <button *ngIf="isSigned()" (click)="downloadPdf()" 
                  class="w-full h-16 rounded-2xl font-semibold text-lg flex items-center justify-center gap-3 transition-all transform active:scale-[0.98] shadow-lg text-white"
                  style="background: linear-gradient(135deg, #102C41, #1e4b6b);">
             <i class="ti ti-download text-2xl"></i>
             DOWNLOAD SIGNED PDF
          </button>

          <p class="text-[10px] text-center text-slate-400 font-bold uppercase tracking-widest pb-10">
            Securely encrypted portal · NZ Plumbing Ltd
          </p>

        </main>
      }

      <!-- Library Picker Modal (Simplified) -->
      @if (showPicker()) {
        <div class="fixed inset-0 bg-[#0b3146]/90 backdrop-blur-md z-[100] flex flex-col animate-in fade-in duration-300">
           <div class="bg-white p-6 pt-14 rounded-b-[40px] shadow-2xl">
              <div class="flex items-center justify-between mb-6">
                 <h2 class="text-2xl font-semibold text-slate-900">{{ isManualMaterial() ? 'Manual Material' : 'Add Material' }}</h2>
                 <button (click)="closePicker()" class="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400"><i class="ti ti-x"></i></button>
              </div>
              <div class="grid grid-cols-2 gap-3 mb-4">
                <button (click)="isManualMaterial.set(false); onSearch()" class="h-12 rounded-2xl font-semibold text-xs uppercase tracking-wider border transition-all"
                        [class.bg-[#2B9AD2]]="!isManualMaterial()" [class.text-white]="!isManualMaterial()" [class.border-[#2B9AD2]]="!isManualMaterial()"
                        [class.bg-slate-50]="isManualMaterial()" [class.text-slate-500]="isManualMaterial()" [class.border-slate-100]="isManualMaterial()">
                  Library
                </button>
                <button (click)="startManualMaterial()" class="h-12 rounded-2xl font-semibold text-xs uppercase tracking-wider border transition-all"
                        [class.bg-[#2B9AD2]]="isManualMaterial()" [class.text-white]="isManualMaterial()" [class.border-[#2B9AD2]]="isManualMaterial()"
                        [class.bg-slate-50]="!isManualMaterial()" [class.text-slate-500]="!isManualMaterial()" [class.border-slate-100]="!isManualMaterial()">
                  Manual
                </button>
              </div>
              @if (!isManualMaterial()) {
                <input type="text" [(ngModel)]="searchQuery" (input)="onSearch()" placeholder="Search library..." class="w-full h-14 px-5 bg-slate-50 border border-slate-100 rounded-2xl font-bold focus:bg-white focus:border-[#2B9AD2] outline-none transition-all">
              } @else {
                <div class="space-y-3">
                  <input type="text" [(ngModel)]="manualMaterialName" placeholder="Material name" class="w-full h-14 px-5 bg-slate-50 border border-slate-100 rounded-2xl font-bold focus:bg-white focus:border-[#2B9AD2] outline-none transition-all">
                  <div class="grid grid-cols-3 gap-3">
                    <input type="number" min="1" step="1" [(ngModel)]="manualMaterialQty" placeholder="Qty" class="h-14 px-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold focus:bg-white focus:border-[#2B9AD2] outline-none">
                    <select [(ngModel)]="manualMaterialUnit" class="h-14 px-3 bg-slate-50 border border-slate-100 rounded-2xl font-bold focus:bg-white focus:border-[#2B9AD2] outline-none">
                      @for (u of units; track u) { <option [value]="u">{{u}}</option> }
                    </select>
                    <input type="number" min="0" step="0.01" [(ngModel)]="manualMaterialPrice" placeholder="Price" class="h-14 px-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold focus:bg-white focus:border-[#2B9AD2] outline-none">
                  </div>
                  <button (click)="addManualMaterial()" [disabled]="!manualMaterialName.trim() || manualMaterialPrice() < 0 || manualMaterialQty() <= 0"
                          class="w-full h-14 rounded-2xl bg-[#102C41] text-white font-semibold disabled:opacity-40">
                    ADD MANUAL MATERIAL
                  </button>
                </div>
              }
           </div>
           @if (!isManualMaterial()) {
           <div class="flex-1 overflow-auto p-5 space-y-3 pb-24">
              @if (isSearching()) {
                <div class="py-20 flex flex-col items-center justify-center text-white/50">
                  <i class="ti ti-loader animate-spin text-4xl mb-2"></i>
                  <p class="font-bold">Searching materials...</p>
                </div>
              }
              @for (m of libraryResults(); track m.id) {
                <div (click)="addFromLibrary(m)" class="bg-white/10 border border-white/10 p-4 rounded-3xl flex items-center gap-4 active:bg-white/20">
                  <div class="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden shrink-0"><img [src]="m.photo_url || defaultPhoto" class="w-full h-full object-cover opacity-70"></div>
                  <div class="flex-1"><h4 class="text-white font-bold leading-tight">{{m.name}}</h4><p class="text-xs text-white/40 font-mono">{{m.max_sell_price | currency:'NZD'}}</p></div>
                  <i class="ti ti-plus text-emerald-400"></i>
                </div>
              }
              @if (!isSearching() && libraryResults().length === 0) {
                <div class="py-20 text-center text-white/30">
                  <i class="ti ti-package-off text-6xl mb-2 block"></i>
                  <p class="font-bold tracking-widest uppercase text-xs">No materials found</p>
                </div>
              }
           </div>
           }
        </div>
      }
    </div>
  `
})
export class FieldQuotingComponent implements OnInit, AfterViewInit {
  api = inject(ApiService);
  route = inject(ActivatedRoute);
  router = inject(Router);

  jobId = signal<string>('');
  returnJobId = signal<string>('');
  job = signal<any>(null);
  durationHours = signal<number>(0);
  labourLines = signal<LabourLine[]>([]);
  isFixedRateJob = signal(false);
  cartItems = signal<any[]>([]);
  
  isLoading = signal(true);
  isSaving = signal(false);
  isSigned = signal(false);
  defaultPhoto = 'https://nzplumbinghq.co.nz/api/assets/default_mat.jpeg';

  // Picker
  showPicker = signal(false);
  isManualMaterial = signal(false);
  searchQuery = '';
  libraryResults = signal<any[]>([]);
  isSearching = signal(false);
  private searchTimer: ReturnType<typeof setTimeout> | undefined;
  manualMaterialName = '';
  manualMaterialQty = signal(1);
  manualMaterialUnit = 'EA';
  manualMaterialPrice = signal(0);
  units = ['EA', 'PR', 'EACH', 'MET', 'LEN', 'PACK', 'ROLL', 'COIL', 'SHT', 'BOX', 'JAR', 'SET', 'BAG', 'KIT', 'PKT', 'DAY'];

  canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('signatureCanvas') set signatureCanvasRef(ref: ElementRef<HTMLCanvasElement> | undefined) {
    if (ref) {
      this.canvasRef = ref;
      setTimeout(() => this.setupSignaturePad(), 50);
    }
  }
  private ctx!: CanvasRenderingContext2D | null;
  private isDrawing = false;
  hasSignature = signal(false);

  canBuildQuote(): boolean {
    const currentJob = this.job();
    if (!currentJob) return false;
    if (this.isLinkedInspectionView()) {
      return this.isSigned();
    }
    if (currentJob.type === 'Inspection' || currentJob.job_type === 'Inspection') {
      return currentJob.status !== 'Unassigned';
    }
    return ['Ready for Office', 'Completed'].includes(currentJob.status);
  }

  isLinkedInspectionView(): boolean {
    const currentJob = this.job();
    return !!this.returnJobId() && (currentJob?.type === 'Inspection' || currentJob?.job_type === 'Inspection');
  }

  canEditQuote(): boolean {
    return this.canBuildQuote() && !this.isSigned() && !this.isLinkedInspectionView();
  }

  backRoute(): any[] {
    return ['/job', this.returnJobId() || this.jobId()];
  }

  ngOnInit() {
    this.route.queryParamMap.subscribe(params => {
      this.returnJobId.set(params.get('return_job_id') || '');
    });
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.jobId.set(id);
        this.loadContext(id);
      }
    });
  }

  loadContext(id: string) {
    this.isLoading.set(true);
    this.api.getQuoteContext(id, 'Job', this.returnJobId()).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.job.set(res.data.job);
          this.durationHours.set(res.data.duration_hours || 0);
          
          // Generate Labour Lines
          const isFixedRate = (res.data.pricing_type || res.data.job?.pricing_type) === 'Fixed';
          this.isFixedRateJob.set(isFixedRate);
          const labourMultiplier = res.data.job?.type === 'Emergency' && Number(res.data.job?.emergency_double_rate || 0) === 1 ? 2 : 1;
          const recordedHours = Number(res.data.duration_hours || res.data.job?.actual_hours || 0);
          const lines = isFixedRate
            ? [{
                workerId: 0,
                name: 'Fixed Rate Job',
                rate: Number(res.data.fixed_labour_charge || res.data.job?.fixed_labour_charge || 0) * labourMultiplier,
                hours: 1,
                workDetails: `Fixed-rate labour charge${labourMultiplier === 2 ? ' with the emergency 2× multiplier' : ''}. ${recordedHours.toFixed(2)} actual hour(s) recorded for internal costing; customer billing is not hourly.`
              }]
            : (res.data.workers || []).map((w: any) => ({
                workerId: w.id,
                name: w.name,
                rate: Number(w.hourly_rate || 0) * labourMultiplier,
                hours: Number(w.duration_hours ?? res.data.duration_hours ?? 0),
                workDetails: 'Plumbing Labour'
              }));
          this.labourLines.set(lines);

          // Import Materials (Map item_name to name for UI consistency)
          this.cartItems.set((res.data.materials || []).map((m: any) => ({
            ...m,
            name: m.item_name || m.name // Support both patterns
          })));

          // OVERRIDE with existing quote if present
          if (res.data.existing_quote) {
            const eq = res.data.existing_quote;
            this.isSigned.set(!!eq.signature_data);
            
            // Map Quote Items back to LabourLines and CartItems
            const labour: LabourLine[] = [];
            const cart: any[] = [];
            
            (eq.items || []).forEach((it: any) => {
               if (it.sku === 'LABOUR') {
                  labour.push({
                    workerId: 0, // Not strictly needed for display
                    name: it.title.replace('Labour: ', ''),
                    rate: parseFloat(it.unit_price),
                    hours: parseFloat(it.quantity),
                    workDetails: it.description
                  });
               } else {
                  cart.push({
                     id: it.id,
                     material_id: it.material_id,
                     name: it.title,
                     quantity: parseFloat(it.quantity),
                     unit: it.unit,
                     sell_price: parseFloat(it.unit_price)
                  });
               }
            });

            if (labour.length > 0) this.labourLines.set(labour);
            if (cart.length > 0) this.cartItems.set(cart);
          }
        }
        this.isLoading.set(false);
      },
      error: () => {
        showToast('error', 'Failed to load quote context');
        this.isLoading.set(false);
      }
    });
  }

  subTotal = computed(() => {
    const labour = this.labourLines().reduce((sum, l) => sum + (l.rate * l.hours), 0);
    const materials = this.cartItems().reduce((sum, m) => sum + (m.quantity * m.sell_price), 0);
    return labour + materials;
  });

  removeMaterial(index: number) {
    this.cartItems.update(items => items.filter((_, i) => i !== index));
  }

  openPicker() {
    this.showPicker.set(true);
    this.isManualMaterial.set(false);
    this.onSearch();
  }

  closePicker() {
    this.showPicker.set(false);
    this.isManualMaterial.set(false);
    this.manualMaterialName = '';
    this.manualMaterialQty.set(1);
    this.manualMaterialUnit = 'EA';
    this.manualMaterialPrice.set(0);
  }

  startManualMaterial() {
    this.isManualMaterial.set(true);
    this.manualMaterialName = '';
    this.manualMaterialQty.set(1);
    this.manualMaterialUnit = 'EA';
    this.manualMaterialPrice.set(0);
  }

  onSearch() {
    const term = this.searchQuery.trim();
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (term.length > 0 && term.length < 2) {
      this.libraryResults.set([]);
      this.isSearching.set(false);
      return;
    }

    this.isSearching.set(true);
    this.searchTimer = setTimeout(() => {
      this.api.getMaterials({ search: term, limit: 50 }).subscribe({
        next: (res: any) => {
          this.libraryResults.set(res.data?.materials || []);
          this.isSearching.set(false);
        },
        error: () => {
          this.libraryResults.set([]);
          this.isSearching.set(false);
        }
      });
    }, 250);
  }

  addFromLibrary(m: any) {
    this.cartItems.update(items => [...items, {
      id: 0,
      material_id: m.id,
      name: m.name,
      quantity: 1,
      unit: m.unit,
      sell_price: m.max_sell_price,
      photo_url: m.photo_url
    }]);
    this.closePicker();
  }

  addManualMaterial() {
    const name = this.manualMaterialName.trim();
    if (!name) {
      showToast('error', 'Material name is required');
      return;
    }
    if (this.manualMaterialQty() <= 0) {
      showToast('error', 'Quantity must be greater than zero');
      return;
    }
    if (this.manualMaterialPrice() < 0) {
      showToast('error', 'Sell price cannot be negative');
      return;
    }

    this.cartItems.update(items => [...items, {
      id: 0,
      material_id: null,
      name,
      quantity: this.manualMaterialQty(),
      unit: this.manualMaterialUnit,
      sell_price: this.manualMaterialPrice(),
      photo_url: null
    }]);
    this.closePicker();
  }

  submitQuote() {
    if (!this.canvasRef) {
      showToast('error', 'Signature pad is still loading. Please try again.');
      return;
    }
    this.isSaving.set(true);
    const signatureBase64 = this.canvasRef.nativeElement.toDataURL('image/png');

    // Combine Labour and Materials for the Quote Items table
    const items = [
      ...this.labourLines().map(l => ({
        sku: 'LABOUR',
        title: `Labour: ${l.name}`,
        desc: l.workDetails,
        price: l.rate,
        qty: l.hours
      })),
      ...this.cartItems().map(m => ({
        sku: 'MATERIAL',
        material_id: m.material_id || null,
        title: m.name,
        desc: '',
        price: m.sell_price,
        qty: m.quantity,
        unit: m.unit || 'EA',
        photo_url: m.photo_url || null
      }))
    ];

    const payload = {
      reference_id: this.jobId(),
      model_type: 'Job',
      customer_name: this.job()?.customer_name || 'Valued Customer',
      job_address: this.job()?.address || '',
      status: 'Sent',
      items: items,
      subtotal: this.subTotal(),
      tax: this.subTotal() * 0.15,
      total: this.subTotal() * 1.15,
      signature_data: signatureBase64 // Sent for PDF generation
    };

    this.api.createQuote(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
           // Generate PDF
           this.api.generateQuotePdf(res.data.id, payload).subscribe((pdfRes: any) => {
              this.isSaving.set(false);
              if (pdfRes.success) {
                 this.openPdfFromResponse(pdfRes.data);
                 this.markJobAfterQuote();
              } else {
                 showToast('error', pdfRes.message || 'Quote saved, but PDF generation failed.');
              }
           }, () => {
              this.isSaving.set(false);
              showToast('error', 'Quote saved, but PDF generation failed.');
           });
        } else {
           this.isSaving.set(false);
           showToast('error', res.message);
        }
      },
      error: () => {
        this.isSaving.set(false);
        showToast('error', 'Network error saving quote');
      }
    });
  }

  private markJobAfterQuote() {
    const currentJob = this.job();
    const isInspection = currentJob?.type === 'Inspection' || currentJob?.job_type === 'Inspection';
    const nextStatus = isInspection ? 'Quote Sent' : 'Ready for Office';

    this.api.updateJobStatus({ id: this.jobId(), status: nextStatus }).subscribe({
      next: () => {
        showToast('success', isInspection ? 'Quote sent for customer approval!' : 'Final quote sent to office!');
        this.router.navigate(['/job', this.jobId()]);
      },
      error: () => {
        showToast('error', 'Quote saved, but job status was not updated.');
        this.router.navigate(['/job', this.jobId()]);
      }
    });
  }

  ngAfterViewInit() {
    this.setupSignaturePad();
  }

  private setupSignaturePad() {
    if (!this.canvasRef || this.isSigned()) return;
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const ratio = window.devicePixelRatio || 1;
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    this.ctx = canvas.getContext('2d');
    if (this.ctx) {
      this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      this.ctx.lineWidth = 3;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.strokeStyle = '#0b3146';
    }

    canvas.ontouchstart = (e) => this.handleStart(e);
    canvas.ontouchmove = (e) => this.handleMove(e);
    canvas.ontouchend = () => this.handleEnd();
    canvas.onmousedown = (e) => this.handleStart(e);
    canvas.onmousemove = (e) => this.handleMove(e);
    canvas.onmouseup = () => this.handleEnd();
    canvas.onmouseleave = () => this.handleEnd();
  }

  private handleStart(e: any) {
    e.preventDefault();
    this.isDrawing = true;
    this.hasSignature.set(true);
    const pos = this.getPos(e);
    this.ctx?.beginPath();
    this.ctx?.moveTo(pos.x, pos.y);
  }
  private handleMove(e: any) {
    if (!this.isDrawing) return;
    const pos = this.getPos(e);
    this.ctx?.lineTo(pos.x, pos.y);
    this.ctx?.stroke();
  }
  private handleEnd() { this.isDrawing = false; }
  private getPos(e: any) {
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }
  clearSignature() {
    this.ctx?.clearRect(0, 0, this.canvasRef.nativeElement.width, this.canvasRef.nativeElement.height);
    this.hasSignature.set(false);
  }

  downloadPdf() {
    this.api.getQuoteContext(this.jobId(), 'Job', this.returnJobId()).subscribe(res => {
       if (res.success && res.data.existing_quote) {
          const eqId = res.data.existing_quote.id;
          this.api.generateQuotePdf(eqId, {}).subscribe((pdfRes: any) => {
            if (pdfRes.success && pdfRes.data?.pdf_url) {
              this.openPdfFromResponse(pdfRes.data);
            } else {
              showToast('error', pdfRes.message || 'Failed to generate PDF.');
            }
          }, () => showToast('error', 'Failed to generate PDF.'));
       }
    });
  }

  private openPdfFromResponse(data: any) {
    if (data?.pdf_base64) {
      const byteCharacters = atob(data.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }
    if (data?.pdf_url) {
      window.open('https://nzplumbinghq.co.nz/api' + data.pdf_url, '_blank');
    }
  }
}
